import { NotificationMessage } from '@gravitate-js/excalibrr'
import { useApi } from '@gravitate-js/the-armory'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getAPIURL } from '@utils/api'
import { message } from 'antd'

export const useSites = () => {
  const api = useApi({ baseURLOverride: getAPIURL() })
  const queryClient = useQueryClient()

  const useSiteOverviewQuery = (filter) =>
    useQuery({
      queryKey: ['siteOverviewQuery', filter],
      queryFn: ({ queryKey }) =>
        api.post('store/overview', {
          filter: { ...queryKey[1], store_number: queryKey[1]?.store_number?.toString() },
        }),
      refetchOnWindowFocus: false,
    })

  const updateSiteMutation = () =>
    useMutation({
      mutationFn: (site) => api.post('store/update', site),
      // When mutate is called:
      onSuccess: async (newSite) => {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries({
          queryKey: ['siteOverviewQuery'],
        })

        // Snapshot the previous value
        const previousQueries = queryClient.getQueriesData({
          queryKey: ['siteOverviewQuery'],
        })

        // Optimistically update to the new value
        queryClient.setQueriesData({ queryKey: ['siteOverviewQuery'] }, (oldData) => {
          return {
            ...oldData,
            rows: oldData.rows.map((site) => (newSite.id === site.id ? { ...site, ...newSite } : site)),
          }
        })
        message.success('Success!  Site Updated.')
        return { previousQueries, newSite }
      },
      onError: (err, newSite, context) => {
        queryClient.setQueriesData({ queryKey: ['siteOverviewQuery'] }, context.previousQueries)
      },
    })

  const uploadPDFMutation = useMutation({
    mutationFn: (request) =>
      api.uploadFile('store/upload_layout', request.file, {
        queryParams: new URLSearchParams({ store_id: request.store_id }),
      }),
    onSuccess: async (newSite) => {
      updateSite(queryClient, newSite)
    },
  })

  const removePDFMutation = useMutation({
    mutationFn: (request) =>
      api.post('store/remove_layout', null, { queryParams: new URLSearchParams({ store_id: request.store_id }) }),
    onSuccess: async (newSite) => {
      updateSite(queryClient, newSite)
    },
  })

  const createSiteMutation = () =>
    useMutation({
      mutationFn: (site) => api.post('store/create', site),
      onSuccess: async (newSite, request) => {
        // When create succeeds
        // update to the new value
        queryClient.setQueriesData({ queryKey: ['siteOverviewQuery'] }, (oldData) => {
          return {
            ...oldData,
            rows: [
              {
                ...newSite,
                ...request,
                supply_owner_id: request.counterparty_id,
                supply_owner_name: request.counterparty_name,
                freight_customer_id: request.counterparty_id,
                freight_customer_name: request.counterparty_name,
              },
              ...oldData.rows,
            ],
          }
        })
        NotificationMessage('Success', 'New item created.', false)
        // Return a context with the previous and new todo
        return { newSite }
      },
    })

  const useSiteListQuery = () =>
    useQuery({
      queryKey: ['siteListQuery'],
      queryFn: () => api.post('store/store_view', {}),
      refetchOnWindowFocus: false,
      staleTime: 3 * 60 * 1000,
    })

  const useSplitPartnersQuery = (id) =>
    useQuery({
      queryKey: ['splitPartnersQuery', id],
      queryFn: () => api.post('store/split_partners', {}, { queryParams: new URLSearchParams({ site_id: id }) }),
      enabled: !!id,
    })

  const updateSplitPartnersMutation = useMutation({
    mutationKey: ['updateSplitPartnersMutation'],
    mutationFn: (request) => api.post('store/update_split_partner', request),
    onSuccess: async (_, request) => {
      await queryClient.invalidateQueries({
        queryKey: ['splitPartnersQuery', request.site_id],
      })
    },
  })

  return {
    useSiteOverviewQuery,
    updateSiteMutation,
    createSiteMutation,
    useSiteListQuery,
    uploadPDFMutation,
    removePDFMutation,
    useSplitPartnersQuery,
    updateSplitPartnersMutation,
  }
}

const updateSite = async (queryClient, newSite) => {
  // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
  await queryClient.cancelQueries({
    queryKey: ['siteOverviewQuery'],
  })

  // Optimistically update to the new value
  queryClient.setQueriesData({ queryKey: ['siteOverviewQuery'] }, (oldData) => {
    if (!oldData) return
    return {
      ...oldData,
      rows: oldData.rows.map((site) => (newSite.id === site.id ? { ...site, ...newSite } : site)),
    }
  })
}
